import {
  TaxRateFormModal,
  canEditTaxRate,
  taxRateAmountLabel,
  useLocationTaxRates,
} from '@vgrubs/components';
import { LocationMode } from 'components/src/types/location.types';
import { LocationTaxRateEntity } from 'components/src/types/taxRate.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { AmountComponent } from '../components/AmountComponent';
import { AmountsPanel } from '../components/AmountsPanel';

type Props = {
  locationId: string | number;
  /** A `pos` location syncs its rates from the register - nothing to edit. */
  mode: LocationMode | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
  /** Opens the Tax Rates modal, which is where they are added and removed. */
  onManage: () => void;
};

/**
 * The location's taxes, on the page rather than behind the dropdown: one box
 * of figures beside the payout one, and clicking a tax opens it for editing.
 *
 * Reads the same query as the modal, so opening that costs no second request.
 */
export const LocationTaxRatesSummary = ({
  locationId,
  mode,
  setLoading,
  onManage,
}: Props) => {
  const { taxRates, isLoading, invalidate } = useLocationTaxRates(locationId);
  const [editModal, setEditModal] = useState<LocationTaxRateEntity | null>(
    null
  );

  const isPos = mode === LocationMode.pos;

  // No heading and no manage button, like the payout box beside it: a location
  // without taxes shows nothing at all, and they are added from the Tax Rates
  // entry in the page's dropdown.
  if (isLoading || taxRates.length === 0) return null;

  return (
    <>
      <AmountsPanel>
        {taxRates.map((taxRate) => (
          <AmountComponent
            key={taxRate.id}
            title={taxRate.name}
            amount={taxRateAmountLabel(taxRate)}
            subtitle={
              taxRate.applyToAllItems
                ? 'Applies to all items'
                : `Set on ${taxRate.assignments?.length ?? 0} ${
                    taxRate.assignments?.length === 1 ? 'item' : 'items'
                  }`
            }
            // A rate the POS owns has nothing to edit, so it opens the list,
            // where the copy explains why it is read-only.
            onClick={() =>
              canEditTaxRate(taxRate, isPos)
                ? setEditModal(taxRate)
                : onManage()
            }
          />
        ))}
      </AmountsPanel>

      <TaxRateFormModal
        formModal={editModal}
        closeModal={() => setEditModal(null)}
        locationId={locationId}
        setLoading={setLoading}
        onSaved={() => invalidate()}
      />
    </>
  );
};
